import axiosInstance from '../../../services/axiosConfig';
import pageService from './pageService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://app-rmmsapi.azurewebsites.net/api";
const CMD_API_BASE_URL = "https://app-rmmscmdapi.azurewebsites.net/api";

// Get company ID from session/local storage
const getCompanyId = () => {
  const sessionCompanyId = sessionStorage.getItem('TEMPCOM');
  if (sessionCompanyId) {
    try {
      return parseInt(JSON.parse(sessionCompanyId));
    } catch (e) {
      return parseInt(sessionCompanyId);
    }
  }
  const localCompanyId = localStorage.getItem('temp_com_id');
  if (localCompanyId) return parseInt(localCompanyId);
  return 3;
};

const tagService = {
  // Get all tags for dropdown selection (includes tags + vars)
  async getTags(companyId = null) {
    try {
      const effectiveCompanyId = companyId || getCompanyId();
      const allTags = [];

      // Get both tags and vars from DataSource/variables endpoint
      const response = await axiosInstance.get(`${API_BASE_URL}/DataSource/variables?companyId=${effectiveCompanyId}`);
      const payload = response.data || {};
      
      // Process tags
      const tags = payload.Tags || payload.tags || [];
      if (tags.length) {
        allTags.push(...tags.map(t => ({ 
          id: t.TagID ?? t.tagID, 
          name: t.TagName ?? t.tagName,
          description: (t.Description ?? t.description) || '',
          type: 'tag'
        })));
      }
      
      // Process vars
      const vars = payload.Vars || payload.vars || [];
      if (vars.length) {
        allTags.push(...vars.map(v => ({
          id: `var_${v.VarId || v.varId || v.id}`,
          name: `${v.VarName || v.varName || v.name || 'Unknown'}`,
          description: `Variable (Type: ${v.Type || v.type || 'unknown'})`,
          type: 'var',
          originalId: v.VarId || v.varId || v.id,
          originalName: v.VarName || v.varName || v.name,
          varType: v.Type || v.type
        })));
      }
      
      return allTags;
    } catch (error) {
      console.error('Error fetching tags and vars:', error);
      return [];
    }
  },

  // Get tag by id
  async getTagById(id) {
    try {
      const tags = await this.getTags();
      return tags.find(tag => tag.id === id);
    } catch (error) {
      console.error('Error fetching tag:', error);
      return null;
    }
  },

  // Get current tag value for preview mode
  async getTagValue(tagId) {
    try {
      // TODO: Implement actual API call when backend endpoint is ready
      const response = await axiosInstance.get(`${API_BASE_URL}/tagvalues/${tagId}/current`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tag value:', error);
      return null;
    }
  },

  // Get multiple tag values for preview mode
  async getTagValues(tagIds) {
    try {
      const response = await axiosInstance.post(`${API_BASE_URL}/tagvalues/current`, { tagIds });
      return response.data;
    } catch (error) {
      console.error('Error fetching tag values:', error);
      return {};
    }
  },

  // Update tag value (write to PLC/system)
  async updateTagValue(tagId, value, companyId = null) {
    try {
      // Get companyId from parameter or session/local storage
      let effectiveCompanyId = companyId;
      if (!effectiveCompanyId) {
        // Try sessionStorage first (set by TesisSec)
        const sessionCompanyId = sessionStorage.getItem('TEMPCOM');
        if (sessionCompanyId) {
          try {
            effectiveCompanyId = parseInt(JSON.parse(sessionCompanyId));
          } catch (e) {
            effectiveCompanyId = parseInt(sessionCompanyId);
          }
        }
        // Fallback to localStorage
        if (!effectiveCompanyId) {
          const localCompanyId = localStorage.getItem('temp_com_id');
          if (localCompanyId) {
            effectiveCompanyId = parseInt(localCompanyId);
          }
        }
        // Default to 3 if nothing is set
        if (!effectiveCompanyId) {
          effectiveCompanyId = 3;
        }
      }

      // Use cmd/SetValue endpoint for writing tag values
      // API expects: TagId (string), TagValue (string), companyId (int)
      console.log(`[tagService] Updating tag ${tagId} to value "${value}" for companyId=${effectiveCompanyId}`);
      const response = await axiosInstance.post(`${CMD_API_BASE_URL}/cmd/SetValue`, {
        TagId: String(tagId),
        TagValue: String(value),
        companyId: effectiveCompanyId
      });
      console.log(`[tagService] Tag update response:`, response.data);

      // Dispatch event to trigger delayed tag refresh (5 seconds for PLC to process)
      window.dispatchEvent(new CustomEvent('tag-value-updated', {
        detail: { tagId, value, timestamp: Date.now() }
      }));

      return response.data;
    } catch (error) {
      console.error('Error updating tag value:', error);
      throw error;
    }
  },

  // Get tag history for trend chart (last N hours)
  async getTagHistory(tagId, hours = 24) {
    try {
      // Determine if it's a variable or tag
      const isVar = String(tagId).startsWith('var_');
      const actualId = isVar ? String(tagId).replace('var_', '') : tagId;

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date(endDate.getTime() - hours * 60 * 60 * 1000);

      const formatDate = (date) => {
        return date.toISOString().slice(0, 19).replace('T', ' ');
      };

      if (isVar) {
        // Use VarTrend endpoint for variables
        const response = await axiosInstance.post(`${API_BASE_URL}/Tags/GetVarTrend`, {
          VarId: parseInt(actualId),
          StartDate: formatDate(startDate),
          EndDate: formatDate(endDate)
        });
        return response.data || [];
      } else {
        // Use TagTrend endpoint for tags
        const response = await axiosInstance.post(`${API_BASE_URL}/Tags/GetTagTrend`, {
          TagId: parseInt(actualId),
          StartDate: formatDate(startDate),
          EndDate: formatDate(endDate)
        });
        return response.data || [];
      }
    } catch (error) {
      console.error('Error fetching tag history:', error);
      return [];
    }
  },

  // Get all SCADA pages for navigation
  async getSCADAPages() {
    try {
      // Use pageService to get pages from DB
      const pages = await pageService.list();

      // Transform to navigation format
      return pages.map(page => ({
        id: page.id,
        name: page.pageName || `Page ${page.id}`,
        path: `/scada/view/${page.id}`,
        description: page.description || '',
        isActive: page.isActive !== false
      })).filter(page => page.isActive); // Only return active pages
    } catch (error) {
      console.error('Error fetching SCADA pages:', error);
      return [];
    }
  }
};

export default tagService;